import type { CSSProperties } from "react";
import { cn } from "../lib/utils";

type Theme = "light" | "dark" | "system";

interface ThemeToggleProps {
  theme: Theme;
  onChange: (theme: Theme) => void;
  mode?: "icon" | "segmented";
  className?: string;
  style?: CSSProperties;
}

function SunIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
      <circle cx="7.5" cy="7.5" r="2.5" stroke="currentColor" strokeWidth="1.4"/>
      <path d="M7.5 1v1.5M7.5 12.5V14M1 7.5h1.5M12.5 7.5H14M2.9 2.9l1.05 1.05M11.05 11.05l1.05 1.05M2.9 12.1l1.05-1.05M11.05 3.95l1.05-1.05" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M12.5 9A5.5 5.5 0 015 1.5a5.5 5.5 0 100 11 5.5 5.5 0 007.5-3.5z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function SystemIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <rect x="1" y="1.5" width="12" height="8.5" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
      <path d="M4.5 13h5M7 12v1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  );
}

export function ThemeToggle({ theme, onChange, mode = "icon", className, style }: ThemeToggleProps) {
  if (mode === "icon") {
    return (
      <button
        type="button"
        className={cn("tt-icon-btn", className)}
        style={style}
        title={`Current theme: ${theme}. Click to toggle.`}
        onClick={() => onChange(theme === "light" ? "dark" : "light")}
        aria-label="Toggle theme"
      >
        <span className="tt-icon-inner">
          <span className={cn("tt-icon-state", theme === "dark" && "tt-icon-active")}>
            <MoonIcon />
          </span>
          <span className={cn("tt-icon-state", theme !== "dark" && "tt-icon-active")}>
            <SunIcon />
          </span>
        </span>
      </button>
    );
  }

  const options: { value: Theme; icon: JSX.Element }[] = [
    { value: "light",  icon: <SunIcon /> },
    { value: "system", icon: <SystemIcon /> },
    { value: "dark",   icon: <MoonIcon /> },
  ];

  return (
    <div className={cn("tt-seg", className)} style={style} role="group" aria-label="Theme">
      {options.map(opt => (
        <button
          key={opt.value}
          type="button"
          className={cn("tt-seg-btn", theme === opt.value && "tt-seg-active")}
          onClick={() => onChange(opt.value)}
          title={opt.value.charAt(0).toUpperCase() + opt.value.slice(1)}
          aria-pressed={theme === opt.value}
        >
          {opt.icon}
        </button>
      ))}
    </div>
  );
}
